/**
 * EngineTracePanel — admin-only debugging drawer for the playtest engine.
 *
 * Shows a live-streaming list of TraceEntry events emitted by the engine
 * (see src/game/engineTrace.ts). Designed for me (Noah) to watch the engine
 * step through a hand and see exactly which file/line emitted each state
 * change — refactor-safe, since locations are captured from the actual
 * runtime stack, not hardcoded strings.
 *
 * UX overview:
 *   ┌─ Engine Trace ──────── [×] ┐
 *   │ [filter ▾] [search...]      │
 *   │ [auto-scroll ☑] [paused ☐]  │
 *   │ [Clear] [Copy JSON] [Bug]   │
 *   ├─────────────────────────────┤
 *   │ 0042  R3  ▸ buff.fire …     │
 *   │   src/game/playtestEngine.ts:512  [copy] [open]
 *   │   { details... }            │
 *   │ 0043  R3  ▸ damage  …       │
 *   │ ...                         │
 *   └─────────────────────────────┘
 *
 * "Copy as bug report" bundles the most-recent ~50 events into a markdown
 * blob suitable for pasting into a chat message to me — includes timestamp,
 * round, category, summary, source file:line, and details JSON.
 *
 * Implementation notes:
 *   - Subscribes to trace via useEffect + trace.subscribe(). On each emit
 *     we bump a version counter and call trace.all() to re-snapshot.
 *   - Filtering happens client-side. Cheap (n ≤ 2000 ring buffer).
 *   - Auto-scroll: when the user is near the bottom AND auto-scroll is on,
 *     pin to bottom on each update. If they scroll up, we stop snapping
 *     so we don't yank them away from what they were reading.
 *   - "Open" button uses vscode://file/... — works on machines with the
 *     VS Code URL handler installed; otherwise the [copy] button is the
 *     reliable path.
 *   - This component is mounted ONLY for admin users (gated in
 *     PlaytestBoard.tsx), so non-admins pay zero cost.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Bug, X, Copy, Trash2, Download, FileText, Pause, Play, Github, ChevronDown, Settings, Search } from "lucide-react";
import {
  trace,
  formatSource,
  vscodeOpenUrl,
  githubOpenUrl,
  githubSearchCardCodeUrl,
  getBuildCommitSha,
  isPendingDecode,
  KNOWN_CATEGORIES,
  type TraceEntry,
  type TraceCategory,
} from "@/game/engineTrace";
import { cn } from "@/lib/utils";

interface EngineTracePanelProps {
  open: boolean;
  onClose: () => void;
}

/** Color a category dot for quick visual scanning. Pass 2 control-flow
 * categories share teal/lime shades so they're easy to distinguish from
 * Pass 1 state-change categories at a glance. */
function categoryColor(c: TraceCategory): string {
  // Pass 1 — state changes
  if (c === "error") return "bg-red-500";
  if (c.startsWith("buff.")) return "bg-purple-400";
  if (c.startsWith("keyword.")) return "bg-cyan-400";
  if (c === "damage") return "bg-orange-400";
  if (c.startsWith("focus.")) return "bg-blue-400";
  if (c === "stamina") return "bg-emerald-400";
  if (c === "play") return "bg-amber-soft";
  if (c === "round") return "bg-pink-400";
  if (c === "hook.fire") return "bg-fuchsia-400";
  if (c === "override.set") return "bg-indigo-400";
  // Pass 2 — control flow
  if (c === "phase.transition") return "bg-rose-300";
  if (c === "priority.shift") return "bg-teal-300";
  if (c === "pass") return "bg-slate-300";
  if (c.startsWith("chain.")) return "bg-lime-400";
  if (c.startsWith("reaction.")) return "bg-yellow-300";
  if (c.startsWith("counter.")) return "bg-violet-300";
  if (c.startsWith("combat.")) return "bg-orange-300";
  // Pass 3 — cards / zones / status
  if (c.startsWith("deck.")) return "bg-emerald-500";
  if (c.startsWith("hand.")) return "bg-indigo-300";
  if (c.startsWith("sig.")) return "bg-fuchsia-500";
  if (c.startsWith("equip.")) return "bg-amber-500";
  if (c.startsWith("armor.")) return "bg-red-400";
  if (c.startsWith("token.")) return "bg-cyan-300";
  if (c === "utility.activate") return "bg-sky-400";
  if (c.startsWith("status.")) return "bg-purple-500";
  if (c.startsWith("stance.")) return "bg-pink-300";
  // Pass 4 — decisions / dispatch
  if (c.startsWith("prompt.")) return "bg-blue-300";
  if (c === "coin.flip") return "bg-yellow-500";
  if (c === "heal") return "bg-green-400";
  if (c === "applyResults.dispatch") return "bg-slate-500";
  return "bg-slate-400";
}

export function EngineTracePanel({ open, onClose }: EngineTracePanelProps) {
  // Re-render trigger. We don't store the entries array in state — we read
  // trace.all() on every render — but we DO need a re-render signal whenever
  // a new entry is pushed. This counter does that with minimal allocation.
  const [, setVersion] = useState(0);
  const [filter, setFilter] = useState<TraceCategory | "all">("all");
  const [search, setSearch] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Per-row "Open…" menu. Tracks which row's menu is open (by seq) or null.
  const [openMenuFor, setOpenMenuFor] = useState<number | null>(null);
  // Settings popover toggle in the panel header.
  const [showSettings, setShowSettings] = useState(false);
  // Local repo root for building absolute vscode:// URLs. Persists in
  // React state for the session only — sandbox iframe blocks localStorage.
  // User pastes once at the start of the session (e.g.
  // "/Users/noah/dev/guild-masters") and all VS Code links work after.
  const [repoRoot, setRepoRoot] = useState("");

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // When paused we freeze the snapshot so the panel doesn't scroll past
  // what the developer is inspecting. The buffer keeps recording — we just
  // stop reading new events.
  const frozenRef = useRef<readonly TraceEntry[] | null>(null);

  // Subscribe to the trace buffer. Re-render on every push so the panel
  // streams live. Unsubscribe on unmount or when closed (we keep the
  // subscription while open even if paused — the freeze happens at read
  // time, not at subscribe time).
  useEffect(() => {
    if (!open) return;
    return trace.subscribe(() => {
      if (!paused) setVersion((v) => v + 1);
    });
  }, [open, paused]);

  // Close any open per-row “Open…” menu when clicking elsewhere or pressing Esc.
  useEffect(() => {
    if (openMenuFor === null) return;
    const onDown = () => setOpenMenuFor(null);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setOpenMenuFor(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [openMenuFor]);

  // Snapshot of entries to render. When paused we use the frozen array so
  // the list doesn't move; otherwise read fresh from the ring buffer.
  const entries = useMemo<readonly TraceEntry[]>(() => {
    if (paused) {
      if (frozenRef.current === null) frozenRef.current = trace.all().slice();
      return frozenRef.current;
    }
    frozenRef.current = null;
    return trace.all();
    // We deliberately depend on `paused` so resuming flushes the freeze.
    // The version counter (above) drives re-snapshots while live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, open, /* implicit via setVersion */]);

  // Apply filter + search. Filter is exact category match; search is a
  // case-insensitive substring across summary, label, details JSON, source.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (filter !== "all" && e.category !== filter) return false;
      if (!q) return true;
      const hay =
        `${e.summary} ${e.label ?? ""} ${e.category} ${formatSource(e.source)} ${
          e.details ? JSON.stringify(e.details) : ""
        }`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, filter, search]);

  // Auto-scroll to bottom whenever new entries arrive — but only if the
  // user is already near the bottom. If they've scrolled up to read, we
  // don't yank them away.
  useEffect(() => {
    if (!autoScroll) return;
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 80) {
      bottomRef.current?.scrollIntoView({ block: "end" });
    }
  }, [visible.length, autoScroll]);

  // ─── Actions ─────────────────────────────────────────────────────────

  const handleCopySource = async (entry: TraceEntry) => {
    const formatted = formatSource(entry.source);
    try {
      await navigator.clipboard.writeText(formatted);
    } catch {
      /* clipboard unavailable in some sandboxes — fall back silently */
    }
  };

  const handleOpenVscode = (entry: TraceEntry) => {
    const url = vscodeOpenUrl(entry.source, repoRoot || null);
    if (!url) return;
    // Anchor click rather than window.open() because vscode:// is a
    // protocol handler and popup blockers will swallow window.open().
    const a = document.createElement("a");
    a.href = url;
    a.click();
  };

  const handleOpenGithub = (entry: TraceEntry) => {
    const url = githubOpenUrl(entry.source);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleClear = () => {
    trace.clear();
    setExpanded(new Set());
  };

  const handleCopyJson = async () => {
    const payload = visible.map((e) => ({
      seq: e.seq,
      ts: new Date(e.ts).toISOString(),
      relMs: e.relMs,
      round: e.round,
      activePlayer: e.activePlayer,
      category: e.category,
      label: e.label,
      summary: e.summary,
      source: formatSource(e.source),
      details: e.details,
    }));
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch {
      /* ignore */
    }
  };

  const handleDownloadJson = () => {
    const payload = visible.map((e) => ({
      seq: e.seq,
      ts: new Date(e.ts).toISOString(),
      relMs: e.relMs,
      round: e.round,
      activePlayer: e.activePlayer,
      category: e.category,
      label: e.label,
      summary: e.summary,
      source: formatSource(e.source),
      details: e.details,
    }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `engine-trace-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Build a markdown bug report from the last ~50 visible entries. Designed
   * to be pasted directly into a chat with me — gives me full context
   * (round, sequence, source file:line, structured details) without
   * needing to ask follow-up questions.
   */
  const handleCopyBugReport = async () => {
    const tail = visible.slice(-50);
    const lines: string[] = [];
    lines.push(`# Engine Trace Bug Report`);
    lines.push(`Captured: ${new Date().toISOString()}`);
    lines.push(`Total entries shown: ${tail.length} of ${visible.length} (filter=${filter}, search=${search || "—"})`);
    lines.push("");
    lines.push(`## Events (most recent ${tail.length})`);
    lines.push("");
    for (const e of tail) {
      lines.push(
        `### #${e.seq}  ${e.category}${e.label ? ` [${e.label}]` : ""}` +
          (e.round != null ? ` — Round ${e.round}` : "") +
          (e.activePlayer != null ? ` (P${e.activePlayer})` : ""),
      );
      lines.push(`- **summary**: ${e.summary}`);
      lines.push(`- **source**: \`${formatSource(e.source)}\`${e.source.fn ? ` (in \`${e.source.fn}\`)` : ""}`);
      if (e.details && Object.keys(e.details).length > 0) {
        lines.push(`- **details**:`);
        lines.push("```json");
        try {
          lines.push(JSON.stringify(e.details, null, 2));
        } catch {
          lines.push(String(e.details));
        }
        lines.push("```");
      }
      lines.push("");
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
    } catch {
      /* ignore */
    }
  };

  const toggleExpanded = (seq: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="engine-trace-panel">
      <button
        type="button"
        aria-label="Close engine trace"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-[28rem] max-w-full flex-col border-l border-[#2a2f3a] bg-[#0e1014] shadow-2xl">
        {/* ── Header ─────────────────────────────────────────── */}
        <header className="flex items-center justify-between border-b border-[#2a2f3a] px-4 py-3">
          <div className="flex items-center gap-2">
            <Bug size={16} className="text-amber-soft" />
            <h2 className="font-display text-sm font-semibold text-[#e2e8f0]">
              Engine Trace
            </h2>
            <span className="rounded bg-[#22262f] px-1.5 py-0.5 font-mono text-[10px] text-[#94a3b8]">
              {entries.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className={cn(
                "rounded p-1 hover:bg-[#22262f]",
                showSettings ? "text-amber-soft" : "text-[#94a3b8] hover:text-[#e2e8f0]",
              )}
              title="Source-link settings"
              data-testid="button-trace-settings"
            >
              <Settings size={16} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-[#e2e8f0]"
              aria-label="Close"
              data-testid="button-close-trace"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        {/* ── Settings popover ─ source-link config ─────────────── */}
        {showSettings && (
          <div
            className="flex flex-col gap-2 border-b border-[#2a2f3a] bg-[#0b0d11] px-4 py-3 text-[11px]"
            data-testid="trace-settings-panel"
          >
            <div>
              <label className="mb-1 block text-[#cbd5e1]">
                Local repo root (for VS Code links)
              </label>
              <input
                type="text"
                value={repoRoot}
                onChange={(e) => setRepoRoot(e.target.value)}
                placeholder="e.g. /Users/noah/dev/guild-masters"
                className="w-full rounded border border-[#2a2f3a] bg-[#1a1d24] px-2 py-1 font-mono text-[#cbd5e1] placeholder:text-[#475569] focus:border-amber-soft focus:outline-none"
                data-testid="input-repo-root"
                spellCheck={false}
              />
              <p className="mt-1 text-[10px] leading-snug text-[#64748b]">
                Paste the absolute path to your local clone. VS Code links
                will then open the exact line. Session only — paste again
                next time you open the panel.
              </p>
            </div>
            <div className="text-[10px] text-[#64748b]">
              Build commit:{" "}
              <span className="font-mono text-[#94a3b8]">
                {getBuildCommitSha().slice(0, 7)}
              </span>
              {" — GitHub links point at this commit."}
            </div>
          </div>
        )}

        {/* ── Toolbar ────────────────────────────────────────── */}
        <div className="flex flex-col gap-2 border-b border-[#2a2f3a] px-3 py-2 text-[11px]">
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as TraceCategory | "all")}
              className="flex-1 rounded border border-[#2a2f3a] bg-[#1a1d24] px-2 py-1 text-[#cbd5e1] focus:border-amber-soft focus:outline-none"
              data-testid="select-trace-filter"
            >
              <option value="all">All categories</option>
              {KNOWN_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="flex-1 rounded border border-[#2a2f3a] bg-[#1a1d24] px-2 py-1 text-[#cbd5e1] placeholder:text-[#475569] focus:border-amber-soft focus:outline-none"
              data-testid="input-trace-search"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => setAutoScroll((v) => !v)}
              className={cn(
                "flex items-center gap-1 rounded px-2 py-1",
                autoScroll
                  ? "bg-amber-soft/20 text-amber-soft"
                  : "text-[#94a3b8] hover:bg-[#22262f]",
              )}
              title="Auto-scroll to newest"
              data-testid="button-trace-autoscroll"
            >
              ↓ auto
            </button>
            <button
              type="button"
              onClick={() => setPaused((v) => !v)}
              className={cn(
                "flex items-center gap-1 rounded px-2 py-1",
                paused
                  ? "bg-red-500/20 text-red-300"
                  : "text-[#94a3b8] hover:bg-[#22262f]",
              )}
              title={paused ? "Resume live updates" : "Pause live updates"}
              data-testid="button-trace-pause"
            >
              {paused ? <Play size={11} /> : <Pause size={11} />}
              {paused ? "paused" : "live"}
            </button>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-1 rounded px-2 py-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-[#e2e8f0]"
                title="Clear all entries"
                data-testid="button-trace-clear"
              >
                <Trash2 size={11} />
                clear
              </button>
              <button
                type="button"
                onClick={handleCopyJson}
                className="flex items-center gap-1 rounded px-2 py-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-[#e2e8f0]"
                title="Copy filtered entries as JSON"
                data-testid="button-trace-copy-json"
              >
                <Copy size={11} />
                json
              </button>
              <button
                type="button"
                onClick={handleDownloadJson}
                className="flex items-center gap-1 rounded px-2 py-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-[#e2e8f0]"
                title="Download filtered entries as .json"
                data-testid="button-trace-download"
              >
                <Download size={11} />
              </button>
              <button
                type="button"
                onClick={handleCopyBugReport}
                className="flex items-center gap-1 rounded bg-amber-soft/20 px-2 py-1 text-amber-soft hover:bg-amber-soft/30"
                title="Copy last 50 entries as bug report markdown"
                data-testid="button-trace-bug-report"
              >
                <FileText size={11} />
                bug report
              </button>
            </div>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-2 py-2 font-mono text-[11px] leading-relaxed"
          data-testid="trace-list"
        >
          {visible.length === 0 ? (
            <p className="px-2 py-4 text-center text-[#64748b]">
              {entries.length === 0
                ? "No engine events yet. Play a card to start tracing."
                : "No entries match the current filter."}
            </p>
          ) : (
            <ul className="space-y-0.5">
              {visible.map((e) => {
                const isOpen = expanded.has(e.seq);
                const src = formatSource(e.source);
                return (
                  <li
                    key={e.seq}
                    className="rounded border border-transparent px-1.5 py-1 hover:border-[#2a2f3a] hover:bg-[#1a1d24]/60"
                    data-testid={`trace-entry-${e.seq}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpanded(e.seq)}
                      className="flex w-full items-start gap-1.5 text-left"
                    >
                      <span
                        className={cn(
                          "mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                          categoryColor(e.category),
                        )}
                      />
                      <span className="w-10 shrink-0 text-[#475569]">
                        #{e.seq}
                      </span>
                      {e.round != null && (
                        <span className="w-7 shrink-0 text-[#64748b]">
                          R{e.round}
                        </span>
                      )}
                      <span className="w-24 shrink-0 truncate text-[#94a3b8]">
                        {e.category}
                        {e.label && (
                          <span className="text-[#64748b]"> [{e.label}]</span>
                        )}
                      </span>
                      <span className="flex-1 truncate text-[#cbd5e1]">
                        {e.summary}
                      </span>
                      {e.cardName && (
                        <span
                          onClick={(ev) => {
                            ev.stopPropagation();
                            window.open(
                              githubSearchCardCodeUrl(e.cardName!),
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }}
                          className="shrink-0 cursor-pointer truncate rounded bg-amber-soft/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-soft hover:bg-amber-soft/30"
                          title={`Search GitHub for "${e.cardName}" in src/game/ (find hardcoded card-specific logic)`}
                          data-testid={`chip-card-${e.seq}`}
                        >
                          {e.cardName} ↗
                        </span>
                      )}
                    </button>
                    {/* Source line — always visible, distinct row. */}
                    <div className="ml-4 mt-0.5 flex items-center gap-1 text-[10px] text-[#64748b]">
                      <span className="truncate">{src}</span>
                      {e.source.fn && (
                        <span className="truncate text-[#475569]">
                          in {e.source.fn}
                        </span>
                      )}
                      {e.source.file !== "<unknown>" && (
                        <div
                          className="relative ml-auto"
                          onMouseDown={(ev) => ev.stopPropagation()}
                        >
                          {isPendingDecode(e.source) ? (
                            <span
                              className="rounded px-1.5 py-0.5 text-[10px] italic text-[#64748b]"
                              title="Loading sourcemap to decode this frame…"
                              data-testid={`pending-src-${e.seq}`}
                            >
                              decoding…
                            </span>
                          ) : (
                          <button
                            type="button"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setOpenMenuFor((prev) => (prev === e.seq ? null : e.seq));
                            }}
                            className={cn(
                              "flex items-center gap-0.5 rounded px-1.5 py-0.5",
                              openMenuFor === e.seq
                                ? "bg-amber-soft/20 text-amber-soft"
                                : "text-[#94a3b8] hover:bg-[#22262f] hover:text-[#cbd5e1]",
                            )}
                            title="Open this line…"
                            data-testid={`button-open-src-${e.seq}`}
                          >
                            open
                            <ChevronDown size={9} />
                          </button>
                          )}
                          {openMenuFor === e.seq && (
                            <div
                              className="absolute right-0 top-full z-10 mt-1 flex min-w-[180px] flex-col rounded border border-[#2a2f3a] bg-[#0e1014] py-1 text-[11px] shadow-2xl"
                              onClick={(ev) => ev.stopPropagation()}
                              data-testid={`menu-open-src-${e.seq}`}
                            >
                              <button
                                type="button"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleOpenGithub(e);
                                  setOpenMenuFor(null);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-left text-[#cbd5e1] hover:bg-[#22262f]"
                                data-testid={`menu-open-github-${e.seq}`}
                              >
                                <Github size={11} />
                                <span className="flex-1">GitHub</span>
                                <span className="text-[9px] text-[#64748b]">new tab</span>
                              </button>
                              <button
                                type="button"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleOpenVscode(e);
                                  setOpenMenuFor(null);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-left text-[#cbd5e1] hover:bg-[#22262f]"
                                data-testid={`menu-open-vscode-${e.seq}`}
                              >
                                <FileText size={11} />
                                <span className="flex-1">VS Code</span>
                                {!repoRoot && (
                                  <span className="text-[9px] text-amber-soft">
                                    set path
                                  </span>
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleCopySource(e);
                                  setOpenMenuFor(null);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-left text-[#cbd5e1] hover:bg-[#22262f]"
                                data-testid={`menu-copy-src-${e.seq}`}
                              >
                                <Copy size={11} />
                                <span className="flex-1">Copy file:line</span>
                              </button>
                              {e.cardName && (
                                <>
                                  <div className="my-1 border-t border-[#2a2f3a]" />
                                  <button
                                    type="button"
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      window.open(
                                        githubSearchCardCodeUrl(e.cardName!),
                                        "_blank",
                                        "noopener,noreferrer",
                                      );
                                      setOpenMenuFor(null);
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 text-left text-[#cbd5e1] hover:bg-[#22262f]"
                                    data-testid={`menu-search-card-code-${e.seq}`}
                                  >
                                    <Search size={11} />
                                    <span className="flex-1 truncate">
                                      Find “{e.cardName}” in code
                                    </span>
                                    <Github size={9} className="text-[#64748b]" />
                                  </button>
                                  {e.cardId ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          window.open(
                                            `/cards/${e.cardId}`,
                                            "_blank",
                                            "noopener,noreferrer",
                                          );
                                          setOpenMenuFor(null);
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 text-left text-[#cbd5e1] hover:bg-[#22262f]"
                                        data-testid={`menu-open-card-${e.seq}`}
                                      >
                                        <FileText size={11} />
                                        <span className="flex-1 truncate">
                                          Open {e.cardName} (in app)
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          window.open(
                                            `/cards/${e.cardId}/edit`,
                                            "_blank",
                                            "noopener,noreferrer",
                                          );
                                          setOpenMenuFor(null);
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 text-left text-[#cbd5e1] hover:bg-[#22262f]"
                                        data-testid={`menu-edit-card-${e.seq}`}
                                      >
                                        <FileText size={11} />
                                        <span className="flex-1 truncate">
                                          Edit {e.cardName}
                                        </span>
                                      </button>
                                    </>
                                  ) : (
                                    <span className="px-3 py-1.5 text-[10px] italic text-[#64748b]">
                                      Card id not resolved for {e.cardName}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isOpen && e.details && Object.keys(e.details).length > 0 && (
                      <pre className="ml-4 mt-1 overflow-x-auto rounded bg-[#1a1d24] px-2 py-1 text-[10px] text-[#a3b3c9]">
                        {safeStringify(e.details)}
                      </pre>
                    )}
                  </li>
                );
              })}
              <div ref={bottomRef} />
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

/** JSON.stringify that won't throw on circular refs — falls back to String(). */
function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
